import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { SchemaGenerator } from '../../components/SchemaGenerator.js';
import { Footer } from '../../components/Footer.js';
import { MainNavigation } from '../../components/MainNavigation';
import { GridBackground } from '../../components/GridBackground';
import { CTA1, CTA2 } from '../../components/CTABanners';
import { AISnippetBlockFeatured } from '../../components/AISnippetBlock';
import { PillarContentRenderer } from '../../components/PillarContentRenderer';
import { ArticleFooter } from '../../components/ArticleFooter';
import { enableDeveloperOptionsFireStick } from '../../data/questionPages/enableDeveloperOptionsFireStick';

export const EnableDeveloperOptionsFireStickPage = () => {
  const [expandedStep, setExpandedStep] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  // Schema generation for all required types
  const schemas = [
    {
      type: 'Article',
      data: {
        headline: enableDeveloperOptionsFireStick.title,
        description: enableDeveloperOptionsFireStick.metaDescription,
        image: enableDeveloperOptionsFireStick.schema.article.image,
        author: enableDeveloperOptionsFireStick.schema.article.author,
        datePublished: enableDeveloperOptionsFireStick.schema.article.datePublished,
        dateModified: enableDeveloperOptionsFireStick.schema.article.dateModified
      }
    },
    {
      type: 'FAQ',
      data: {
        faqs: enableDeveloperOptionsFireStick.faqs.map(faq => ({
          question: faq.question,
          answer: faq.answer
        }))
      }
    },
    {
      type: 'HowTo',
      data: enableDeveloperOptionsFireStick.schema.howTo
    },
    {
      type: 'VideoObject',
      data: enableDeveloperOptionsFireStick.schema.video
    },
    {
      type: 'BreadcrumbList',
      data: {
        breadcrumbs: enableDeveloperOptionsFireStick.breadcrumbs
      }
    }
  ];

  return (
    <>
      <Helmet>
        <title>{enableDeveloperOptionsFireStick.title}</title>
        <meta name="description" content={enableDeveloperOptionsFireStick.metaDescription} />
        <meta property="og:title" content={enableDeveloperOptionsFireStick.title} />
        <meta property="og:description" content={enableDeveloperOptionsFireStick.metaDescription} />
        <meta property="og:image" content={enableDeveloperOptionsFireStick.schema.article.image} />
        <meta name="keywords" content="enable developer options fire stick, firestick developer mode, adb debugging firestick, apps from unknown sources" />
        <link rel="canonical" href={`https://xtreamcube.com/blog/${enableDeveloperOptionsFireStick.slug}`} />
      </Helmet>

      {/* Schema Markup */}
      {schemas.map((schema, idx) => (
        <SchemaGenerator key={idx} type={schema.type} data={schema.data} />
      ))}

      <div className="min-h-screen relative">
        <GridBackground />
        <MainNavigation />

        {/* Main Content */}
        <motion.div
          className="px-4 py-8 relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <motion.div variants={itemVariants} className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 bg-primary/20 border border-primary/30 text-primary text-sm font-semibold rounded-full">
                  {enableDeveloperOptionsFireStick.category}
                </span>
                <span className="text-slate-400 text-sm">{enableDeveloperOptionsFireStick.readTime} read</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                {enableDeveloperOptionsFireStick.title}
              </h1>
              <p className="text-xl text-slate-300">
                Complete step-by-step guide to enabling Developer Options on Fire Stick. Learn how to sideload IPTV apps, unlock advanced features, and install apps not available in the Amazon Appstore.
              </p>
            </motion.div>

            {/* Quick Answer Block - Using AISnippetBlockFeatured for consistency */}
            <motion.div variants={itemVariants} className="mb-8">
              <AISnippetBlockFeatured
                title="Quick Answer"
                quickAnswer={enableDeveloperOptionsFireStick.quickAnswer}
                keyPoints={[
                  "Go to Settings > Device & Accessories > Developer Options",
                  "Enable ADB Debugging first",
                  "Then enable Apps from Unknown Sources",
                  "Allows sideloading IPTV apps like XtreamCube"
                ]}
                lastUpdated={enableDeveloperOptionsFireStick.lastUpdated}
              />
            </motion.div>

            {/* Video Embed Section (Top Third) */}
            {enableDeveloperOptionsFireStick.video.enabled && (
              <motion.div variants={itemVariants} className="mb-12">
                <div className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700 shadow-xl">
                  <div className="relative pt-[56.25%]">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={enableDeveloperOptionsFireStick.video.embeddedUrl}
                      title={enableDeveloperOptionsFireStick.video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-4 bg-slate-700/50">
                    <p className="text-white font-semibold mb-2">{enableDeveloperOptionsFireStick.video.title}</p>
                    <p className="text-slate-300 text-sm">{enableDeveloperOptionsFireStick.video.description}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Expanded Answer */}
            <motion.div variants={itemVariants} className="mb-12">
              <div className="bg-slate-800/50 border-l-4 border-orange-500 p-6 rounded-r-lg">
                <p className="text-slate-200 leading-relaxed">
                  {enableDeveloperOptionsFireStick.expandedAnswer}
                </p>
              </div>
            </motion.div>

            {/* Step-by-Step Setup Guide */}
            <motion.div variants={itemVariants} className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Complete Step-by-Step Guide</h2>
              <div className="space-y-4">
                {enableDeveloperOptionsFireStick.setupGuide.map((step) => (
                  <motion.div
                    key={step.position}
                    className="bg-slate-800 border border-slate-700 rounded-lg p-5 hover:border-orange-500/50 transition-colors cursor-pointer"
                    onClick={() => setExpandedStep(expandedStep === step.position ? null : step.position)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500">
                          <span className="text-white font-bold text-lg">{step.position}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white">{step.name}</h3>
                        {expandedStep === step.position && (
                          <p className="text-slate-300 mt-3 text-base leading-relaxed">{step.text}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Primary CTA */}
            <motion.div variants={itemVariants} className="mb-12">
              <CTA2
                headline="Start Streaming Today"
                subtext="Now that Developer Options are enabled, try XtreamCube IPTV with 24-hour free trial"
                buttonText={enableDeveloperOptionsFireStick.ctas.primary.text}
                buttonUrl={enableDeveloperOptionsFireStick.ctas.primary.url}
              />
            </motion.div>

            {/* Main Content Sections */}
            {Object.entries(enableDeveloperOptionsFireStick.mainContent).map(([key, section], idx) => (
              <motion.div key={key} variants={itemVariants} className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">{section.heading}</h2>
                <div className="prose prose-invert max-w-none">
                  <PillarContentRenderer content={section.content} />
                </div>
              </motion.div>
            ))}

            {/* Secondary CTA */}
            <motion.div variants={itemVariants} className="mb-12">
              <CTA1
                headline="Need More Help with Firestick?"
                subtext="Check out our complete setup guide for all IPTV apps"
                buttonText={enableDeveloperOptionsFireStick.ctas.secondary.text}
                buttonUrl={enableDeveloperOptionsFireStick.ctas.secondary.url}
                showIcon={false}
              />
            </motion.div>

            {/* Internal Links Section */}
            <motion.div variants={itemVariants} className="mb-12">
              <h3 className="text-2xl font-bold text-white mb-6">Related Guides</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {enableDeveloperOptionsFireStick.internalLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    className="bg-slate-800 border border-slate-700 p-4 rounded-lg hover:border-orange-500/50 transition-colors group"
                  >
                    <p className="text-orange-400 group-hover:text-orange-300 font-semibold">{link.text}</p>
                    <p className="text-slate-400 text-sm mt-2">{link.relevance}</p>
                  </a>
                ))}
              </div>
            </motion.div>

            {/* FAQ Section */}
            <motion.div variants={itemVariants} className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">People Also Ask</h2>
              <div className="space-y-3">
                {enableDeveloperOptionsFireStick.faqs.map((faq, idx) => (
                  <motion.div
                    key={idx}
                    className="bg-slate-800 border border-slate-700 rounded-lg p-5"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <h3 className="font-semibold text-white mb-2">{faq.question}</h3>
                    <p className="text-slate-300 text-sm">{faq.answer}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Meta Information - hidden from UI but available in schema */}

            {/* Article Footer - Share & Navigation */}
            <ArticleFooter
              title={enableDeveloperOptionsFireStick.title}
              nextArticleTitle="Best IPTV Firestick 2026"
              nextArticleUrl="/blog/best-iptv-firestick-2026"
            />
          </div>
        </motion.div>
      </div>

      <Footer />
    </>
  );
};

export default EnableDeveloperOptionsFireStickPage;
